import { MenuGroup, SubMenuItem } from '../types/permission.types';
import { UserRole } from '../types/user.types';

// HQ Module Menu Structure
export const HQ_MENU_STRUCTURE: { [groupId: string]: MenuGroup } = {
  'hq.development': {
    id: 'hq.development',
    name: 'Development',
    description: 'Development management tools',
    cardId: 'hq',
    icon: 'Building2',
    subItems: [
      {
        id: 'hq.development.search',
        name: 'Search Developments',
        description: 'Search and view development details',
        groupId: 'hq.development',
        action: 'ViewDevelopments'
      },
      {
        id: 'hq.development.create',
        name: 'Create Development',
        description: 'Create new development',
        groupId: 'hq.development',
        action: 'CreateDevelopmentForm'
      },
      {
        id: 'hq.development.edit',
        name: 'Edit Development',
        description: 'Edit existing development',
        groupId: 'hq.development',
        action: 'EditDevelopmentForm'
      },
      {
        id: 'hq.development.editUnits',
        name: 'Edit Units in Development',
        description: 'Manage units within developments',
        groupId: 'hq.development',
        action: 'EditUnitsForm'
      },
      {
        id: 'hq.development.viewIncome',
        name: 'View Development Income',
        description: 'View potential income for developments',
        groupId: 'hq.development',
        action: 'ViewPotentialIncome'
      },
      {
        id: 'hq.development.allIncome',
        name: 'All Developments Income',
        description: 'View income across all developments',
        groupId: 'hq.development',
        action: 'AllDevelopmentsIncome'
      },
      {
        id: 'hq.development.delete',
        name: 'Delete Development',
        description: 'Delete development (Super User only)',
        groupId: 'hq.development',
        action: 'DeleteDevelopment',
        requiresPasswordProtection: true
      }
    ]
  },
  'hq.payments': {
    id: 'hq.payments',
    name: 'Payments & Invoicing',
    description: 'Payment and invoice management',
    cardId: 'hq',
    icon: 'Wallet',
    subItems: [
      {
        id: 'hq.payments.proforma',
        name: 'Generate Pro-Forma',
        description: 'Create pro-forma invoices',
        groupId: 'hq.payments',
        action: 'ProFormaInvoiceForm'
      },
      {
        id: 'hq.payments.invoice',
        name: 'Generate Invoice',
        description: 'Convert pro-formas to invoices',
        groupId: 'hq.payments',
        path: '/payments/generate-invoice'
      },
      {
        id: 'hq.payments.payAgent',
        name: 'Pay HQ Agent',
        description: 'Process agent payments',
        groupId: 'hq.payments',
        action: 'PayAgentForm'
      },
      {
        id: 'hq.payments.payPartner',
        name: 'Pay Partner',
        description: 'Process partner payments',
        groupId: 'hq.payments',
        action: 'PayPartnerForm'
      },
      {
        id: 'hq.payments.manageReservationAmounts',
        name: 'Manage Reservation Amounts',
        description: 'Manage reservation amount payments',
        groupId: 'hq.payments',
        action: 'ManageReservationAmounts',
        requiresPasswordProtection: true
      },
      {
        id: 'hq.payments.consultantLoans',
        name: 'Consultant Loans',
        description: 'Manage consultant loans',
        groupId: 'hq.payments',
        action: 'ConsultantLoans',
        requiresPasswordProtection: true
      }
    ]
  },
  'hq.salesTools': {
    id: 'hq.salesTools',
    name: 'Sales Tools',
    description: 'Sales and reservation tools',
    cardId: 'hq',
    icon: 'ClipboardList',
    subItems: [
      {
        id: 'hq.salesTools.preReserve',
        name: 'Pre-Reserve Unit',
        description: 'Create pre-reservations',
        groupId: 'hq.salesTools',
        action: 'PreReserveUnitSearch'
      },
      {
        id: 'hq.salesTools.bulkPreReserve',
        name: 'Bulk Pre-Reserve',
        description: 'Pre-reserve multiple units for one contact',
        groupId: 'hq.salesTools',
        action: 'BulkPreReserve'
      },
      {
        id: 'hq.salesTools.reserve',
        name: 'Reserve Unit',
        description: 'Create reservations',
        groupId: 'hq.salesTools',
        action: 'ReserveUnitSearch'
      },
      {
        id: 'hq.salesTools.captureDeal',
        name: 'Capture a Deal',
        description: 'Capture new deals',
        groupId: 'hq.salesTools',
        action: 'CaptureDealsSearch'
      },
      {
        id: 'hq.salesTools.cancelDeal',
        name: 'Cancel Deal',
        description: 'Cancel existing deals',
        groupId: 'hq.salesTools',
        action: 'CancelDeal',
        requiresPasswordProtection: true
      },
      {
        id: 'hq.salesTools.bookViewing',
        name: 'Book Viewing',
        description: 'Book property viewings',
        groupId: 'hq.salesTools',
        action: 'BookViewingSearch'
      },
      {
        id: 'hq.salesTools.callCentreBook',
        name: 'Book on Consultant Calendar',
        description: 'Book a meeting or call on behalf of a contact',
        groupId: 'hq.salesTools',
        path: '/admin/call-centre-calendar-booking'
      },
      {
        id: 'hq.salesTools.manageReservations',
        name: 'Manage Reservations',
        description: 'Review and approve reservation documents',
        groupId: 'hq.salesTools',
        action: 'ManageReservationsView',
        requiresPasswordProtection: true
      },
      {
        id: 'hq.salesTools.expiredReservations',
        name: 'Expired Reservations',
        description: 'Restore expired reservations from their 14-day snapshots',
        groupId: 'hq.salesTools',
        path: '/expired-reservations'
      },
      {
        id: 'hq.salesTools.manageClientResponses',
        name: 'Manage Client Responses',
        description: 'Review and approve client acceptance documents',
        groupId: 'hq.salesTools',
        action: 'ManageClientResponsesView',
        requiresPasswordProtection: true
      },
      {
        id: 'hq.salesTools.manageOTP',
        name: 'Manage OTP Documents',
        description: 'Review and approve OTP documents',
        groupId: 'hq.salesTools',
        action: 'ManageOTPsView',
        requiresPasswordProtection: true
      }
    ]
  },
  'hq.leads': {
    id: 'hq.leads',
    name: 'Lead Management',
    description: 'Lead capture, import, and assignment tools',
    cardId: 'hq',
    icon: 'TrendingUp',
    subItems: [
      {
        id: 'hq.leads.manualCapture',
        name: 'Manual Lead Capture',
        description: 'Manually capture new leads',
        groupId: 'hq.leads',
        path: '/admin/capture-lead'
      },
      {
        id: 'hq.leads.import',
        name: 'Import Leads',
        description: 'Import leads from CSV/Excel',
        groupId: 'hq.leads',
        path: '/admin/import-contacts'
      },
      {
        id: 'hq.leads.assign',
        name: 'Assign Leads',
        description: 'Assign leads to consultants',
        groupId: 'hq.leads',
        path: '/admin/assign-contacts'
      },
      {
        id: 'hq.leads.creditScores',
        name: 'Credit Scores',
        description: 'View credit score categories for assigned contacts',
        groupId: 'hq.leads',
        path: '/crm/credit-scores'
      },
    ]
  },
  'hq.reports': {
    id: 'hq.reports',
    name: 'Reports',
    description: 'Various reports and analytics',
    cardId: 'hq',
    icon: 'BarChart3',
    subItems: [
      {
        id: 'hq.reports.sales',
        name: 'Sales Reports',
        description: 'Sales performance reports',
        groupId: 'hq.reports',
        action: 'SalesReports'
      },
      {
        id: 'hq.reports.commissions',
        name: 'Commissions Report',
        description: 'Commission calculations and reports',
        groupId: 'hq.reports',
        action: 'CommissionsReport'
      },
      {
        id: 'hq.reports.bonds',
        name: 'Bonds Report',
        description: 'Bond application reports',
        groupId: 'hq.reports',
        action: 'BondsReport'
      },
      {
        id: 'hq.reports.bondsCommission',
        name: 'Bonds Commission Report',
        description: 'Bond commission reports',
        groupId: 'hq.reports',
        action: 'BondsCommissionReport'
      },
      {
        id: 'hq.reports.cash',
        name: 'Cash Deals Report',
        description: 'Cash transaction reports',
        groupId: 'hq.reports',
        action: 'CashDealsReport'
      },
      {
        id: 'hq.reports.cancelled',
        name: 'Cancelled Deals Report',
        description: 'Cancelled deals analysis',
        groupId: 'hq.reports',
        action: 'CancelledDealsReport'
      },
      {
        id: 'hq.reports.mandates',
        name: 'Mandates Report',
        description: 'Mandate performance reports',
        groupId: 'hq.reports',
        action: 'MandatesReport'
      },
      {
        id: 'hq.reports.loans',
        name: 'Loan Report',
        description: 'Consultant loan reports',
        groupId: 'hq.reports',
        action: 'LoanReport'
      },
      {
        id: 'hq.reports.salesCommission',
        name: 'Sales Commission Report',
        description: 'Sales commission reports',
        groupId: 'hq.reports',
        action: 'SalesCommissionReport'
      },
      {
        id: 'hq.reports.leads',
        name: 'Lead Report',
        description: 'Lead generation reports',
        groupId: 'hq.reports',
        action: 'LeadReport'
      },
      {
        id: 'hq.reports.myAllocatedUnits',
        name: 'My Allocated Units',
        description: 'Units allocated to current user',
        groupId: 'hq.reports',
        action: 'MyAllocatedUnitsReport'
      },
      {
        id: 'hq.reports.depositWaiver',
        name: 'Deposit Waiver Report',
        description: 'Deals with deposit waivers or money at an attorney — excluded from sales stats',
        groupId: 'hq.reports',
        action: 'DepositWaiverReport'
      },
    ]
  },
  'hq.statistics': {
    id: 'hq.statistics',
    name: 'Statistics',
    description: 'Performance statistics and analytics',
    cardId: 'hq',
    icon: 'TrendingUp',
    subItems: [
      {
        id: 'hq.statistics.statsHistoryHQ',
        name: 'Stats History HQ',
        description: 'Monthly sales stats with cumulative deal tracking',
        groupId: 'hq.statistics',
        action: 'StatsHistoryHQ'
      },
      {
        id: 'hq.statistics.statsHistoryIndependent',
        name: 'Stats History Independent',
        description: 'Independent consultant performance statistics',
        groupId: 'hq.statistics',
        action: 'StatsHistoryIndependent'
      },
      {
        id: 'hq.statistics.statsHistoryPartner',
        name: 'Stats History Partner',
        description: 'Partner performance statistics',
        groupId: 'hq.statistics',
        action: 'StatsHistoryPartner'
      },
      {
        id: 'hq.statistics.statsHistoryPartnerAgent',
        name: 'Stats History Partner Agent',
        description: 'Partner agent performance statistics',
        groupId: 'hq.statistics',
        action: 'StatsHistoryPartnerAgent'
      }
    ]
  },
  'hq.templates': {
    id: 'hq.templates',
    name: 'Templates',
    description: 'Document templates management',
    cardId: 'hq',
    icon: 'FileCode',
    subItems: [
      {
        id: 'hq.templates.mandate',
        name: 'Mandate',
        description: 'Mandate document template',
        groupId: 'hq.templates',
        action: 'MandateTemplate'
      },
      {
        id: 'hq.templates.reservation',
        name: 'Reservation',
        description: 'Reservation document template',
        groupId: 'hq.templates',
        action: 'ReservationTemplate'
      },
      {
        id: 'hq.templates.consultantContract',
        name: 'Consultant Employment Contract',
        description: 'Consultant employment contract template',
        groupId: 'hq.templates',
        action: 'ConsultantContractTemplate'
      },
      {
        id: 'hq.templates.independentConsultantContract',
        name: 'Call Centre Employment Contract',
        description: 'Call centre agent contract template',
        groupId: 'hq.templates',
        action: 'IndependentConsultantContractTemplate'
      },
      {
        id: 'hq.templates.adminContract',
        name: 'Admin Employment Contract',
        description: 'Admin employment contract template',
        groupId: 'hq.templates',
        action: 'AdminContractTemplate'
      },
      {
        id: 'hq.templates.independentPartnerContract',
        name: 'Independent Partner Contract',
        description: 'Independent partner contract template',
        groupId: 'hq.templates',
        action: 'IndependentPartnerContractTemplate'
      }
    ]
  },
  'hq.developmentDocs': {
    id: 'hq.developmentDocs',
    name: 'Development Docs',
    description: 'Development documentation',
    cardId: 'hq',
    icon: 'FileSearch',
    subItems: [
      {
        id: 'hq.developmentDocs.viewDocs',
        name: 'View Development Docs',
        description: 'View development documentation',
        groupId: 'hq.developmentDocs',
        action: 'DevelopmentDocs'
      }
    ]
  },
};

// Partner Module Menu Structure
export const PARTNER_MENU_STRUCTURE: { [groupId: string]: MenuGroup } = {
  'partner.salesTools': {
    id: 'partner.salesTools',
    name: 'Sales Tools',
    description: 'Sales and reservation tools',
    cardId: 'partner',
    icon: 'ClipboardList',
    subItems: [
      {
        id: 'partner.salesTools.preReserve',
        name: 'Pre-Reserve Unit',
        description: 'Create pre-reservations',
        groupId: 'partner.salesTools',
        action: 'PreReserveUnitSearch'
      },
      {
        id: 'partner.salesTools.reserve',
        name: 'Reserve Unit',
        description: 'Create reservations',
        groupId: 'partner.salesTools',
        action: 'ReserveUnitSearch'
      },
      {
        id: 'partner.salesTools.captureDeal',
        name: 'Capture a Deal',
        description: 'Capture new deals',
        groupId: 'partner.salesTools',
        action: 'CaptureDealsSearch'
      }
    ]
  },
  'partner.reports': {
    id: 'partner.reports',
    name: 'Reports',
    description: 'Partner reports and analytics',
    cardId: 'partner',
    icon: 'BarChart3',
    subItems: [
      {
        id: 'partner.reports.sales',
        name: 'Sales Reports',
        description: 'Partner sales reports',
        groupId: 'partner.reports',
        action: 'SalesReports'
      },
      {
        id: 'partner.reports.commissions',
        name: 'Commissions Report',
        description: 'Partner commission reports',
        groupId: 'partner.reports',
        action: 'CommissionsReport'
      }
    ]
  },
  'partner.statistics': {
    id: 'partner.statistics',
    name: 'Statistics',
    description: 'Partner performance statistics',
    cardId: 'partner',
    icon: 'TrendingUp',
    subItems: [
      {
        id: 'partner.statistics.performance',
        name: 'Performance Stats',
        description: 'Partner performance metrics',
        groupId: 'partner.statistics',
        action: 'StatsHistoryPartner'
      }
    ]
  }
};

// CRM Module Menu Structure  
export const CRM_MENU_STRUCTURE: { [groupId: string]: MenuGroup } = {
  'crm.contacts': {
    id: 'crm.contacts',
    name: 'Contacts',
    description: 'Contact management',
    cardId: 'crm',
    icon: 'Users',
    subItems: [
      {
        id: 'crm.contacts.view',
        name: 'View Contacts',
        description: 'View all contacts',
        groupId: 'crm.contacts',
        action: 'ContactsList'
      },
      {
        id: 'crm.contacts.add',
        name: 'Add Contact',
        description: 'Add new contact',
        groupId: 'crm.contacts',
        action: 'AddContact'
      },
      {
        id: 'crm.contacts.import',
        name: 'Import Contacts',
        description: 'Import contacts from file',
        groupId: 'crm.contacts',
        action: 'ImportContacts'
      }
    ]
  },
  'crm.leads': {
    id: 'crm.leads',
    name: 'Leads',
    description: 'Lead management',
    cardId: 'crm',
    icon: 'UserCheck',
    subItems: [
      {
        id: 'crm.leads.view',
        name: 'View Leads',
        description: 'View all leads',
        groupId: 'crm.leads',
        action: 'LeadsList'
      },
      {
        id: 'crm.leads.assign',
        name: 'Assign Leads',
        description: 'Assign leads to agents',
        groupId: 'crm.leads',
        action: 'AssignLeads'
      },
      {
        id: 'crm.leads.convert',
        name: 'Convert Leads',
        description: 'Convert leads to deals',
        groupId: 'crm.leads',
        action: 'ConvertLeads'
      }
    ]
  },
  'crm.activities': {
    id: 'crm.activities',
    name: 'Activities',
    description: 'Activity tracking',
    cardId: 'crm',
    icon: 'Activity',
    subItems: [
      {
        id: 'crm.activities.view',
        name: 'View Activities',
        description: 'View all activities',
        groupId: 'crm.activities',
        action: 'ActivitiesList'
      },
      {
        id: 'crm.activities.schedule',
        name: 'Schedule Activity',
        description: 'Schedule new activity',
        groupId: 'crm.activities',
        action: 'ScheduleActivity'
      }
    ]
  }
};

// Complete menu structure
export const COMPLETE_MENU_STRUCTURE = {
  hq: HQ_MENU_STRUCTURE,
  partner: PARTNER_MENU_STRUCTURE,
  crm: CRM_MENU_STRUCTURE
};

// Default hierarchical permissions for roles
// Note: Using string index to support both active and deprecated roles
export const DEFAULT_HIERARCHICAL_PERMISSIONS: { [role: string]: any } = {
  [UserRole.PROPCLOUDAI_SUPER_USER]: {
    dashboardCards: {
      hq: {
        enabled: true,
        menuGroups: Object.keys(HQ_MENU_STRUCTURE).reduce((acc, groupId) => {
          acc[groupId] = {
            enabled: true,
            subItems: HQ_MENU_STRUCTURE[groupId].subItems.reduce((subAcc, item) => {
              subAcc[item.id] = true;
              return subAcc;
            }, {} as { [key: string]: boolean })
          };
          return acc;
        }, {} as { [key: string]: { enabled: boolean; subItems: { [key: string]: boolean } } })
      },
      partner: {
        enabled: true,
        menuGroups: Object.keys(PARTNER_MENU_STRUCTURE).reduce((acc, groupId) => {
          acc[groupId] = {
            enabled: true,
            subItems: PARTNER_MENU_STRUCTURE[groupId].subItems.reduce((subAcc, item) => {
              subAcc[item.id] = true;
              return subAcc;
            }, {} as { [key: string]: boolean })
          };
          return acc;
        }, {} as { [key: string]: { enabled: boolean; subItems: { [key: string]: boolean } } })
      },
      crm: {
        enabled: true,
        menuGroups: Object.keys(CRM_MENU_STRUCTURE).reduce((acc, groupId) => {
          acc[groupId] = {
            enabled: true,
            subItems: CRM_MENU_STRUCTURE[groupId].subItems.reduce((subAcc, item) => {
              subAcc[item.id] = true;
              return subAcc;
            }, {} as { [key: string]: boolean })
          };
          return acc;
        }, {} as { [key: string]: { enabled: boolean; subItems: { [key: string]: boolean } } })
      },
      advertising: { enabled: true, menuGroups: {} },
      leadStats: { enabled: true, menuGroups: {} },
      viewingManagement: { enabled: true, menuGroups: {} },
      consultations: { enabled: true, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      syndication_config: { enabled: true, menuGroups: {} },
      whatsapp_followups_config: { enabled: true, menuGroups: {} },
      whatsapp_bot_builder: { enabled: true, menuGroups: {} },
      whatsapp_automation_center: { enabled: true, menuGroups: {} },
      whatsapp_bot_analytics: { enabled: true, menuGroups: {} },
      reviews_admin: { enabled: true, menuGroups: {} },
      consultant_reviews: { enabled: true, menuGroups: {} },
      call_centre_config: { enabled: true, menuGroups: {} },
      dialer_simulation: { enabled: true, menuGroups: {} },
      website_cms: { enabled: true, menuGroups: {} },
      mobile_app_cms: { enabled: true, menuGroups: {} },
      media_manager: { enabled: true, menuGroups: {} },
      credit_score_view: { enabled: true, menuGroups: {} },
      secrets_management: { enabled: true, menuGroups: {} },
      aria_mission_control: { enabled: true, menuGroups: {} },
      management_scoreboard: { enabled: true, menuGroups: {} },
      lead_assignment_history: { enabled: true, menuGroups: {} },
      lead_distribution: { enabled: true, menuGroups: {} },
      notifications_admin: { enabled: true, menuGroups: {} },
      cindy_test_form: { enabled: true, menuGroups: {} },
      voice_lab: { enabled: true, menuGroups: {} },
      heidi_console: { enabled: true, menuGroups: {} },
      wendy_console: { enabled: true, menuGroups: {} },
      aria_training_console: { enabled: true, menuGroups: {} },
      cindy_leads: { enabled: true, menuGroups: {} },
      careers_applications: { enabled: true, menuGroups: {} },
      cindy_analytics: { enabled: true, menuGroups: {} },
      cindy_cost_monitor: { enabled: true, menuGroups: {} },
      consultant_lead_report: { enabled: true, menuGroups: {} },
      investmentProposals: { enabled: true, menuGroups: {} },
      capital_investment: { enabled: true, menuGroups: {} },
      allProposals: { enabled: true, menuGroups: {} },
      buyers_list: { enabled: true, menuGroups: {} },
      custom_audiences: { enabled: true, menuGroups: {} },
      expired_reservations: { enabled: true, menuGroups: {} },
      escalations: { enabled: true, menuGroups: {} },
      booking_outcomes: { enabled: true, menuGroups: {} },
      performance_hub: { enabled: true, menuGroups: {} }
    }
  },
  [UserRole.HQ_CONSULTANT_LVL1]: {
    dashboardCards: {
      hq: {
        enabled: true,
        menuGroups: {
          'hq.salesTools': {
            enabled: true,
            subItems: {
              'hq.salesTools.preReserve': true,
              'hq.salesTools.bulkPreReserve': true,
              'hq.salesTools.reserve': false,  // Consultants cannot reserve, only pre-reserve
              'hq.salesTools.captureDeal': true,
              'hq.salesTools.workOnDeal': false,
              'hq.salesTools.cancelDeal': false,
              'hq.salesTools.bookViewing': true,
              'hq.salesTools.manageReservations': false,
              'hq.salesTools.manageClientResponses': false,
              'hq.salesTools.manageOTP': false,
              'hq.salesTools.expiredReservations': true  // Own snapshots only (enforced by CF + list scoping)
            }
          },
          'hq.reports': {
            enabled: true,
            subItems: {
              'hq.reports.sales': true,        // Own sales only
              'hq.reports.commissions': true,  // Own commissions only
              'hq.reports.commissionsGrouped': false,
              'hq.reports.bonds': false,
              'hq.reports.bondsCommission': false,
              'hq.reports.bondsCommissionGrouped': false,
              'hq.reports.cash': false,
              'hq.reports.cancelled': false,
              'hq.reports.mandates': false,
              'hq.reports.loans': false,
              // Own sales-commission report only. The live rolePermissions doc
              // already grants this — listed here too so a role-doc reset or a
              // fresh environment seeded from these defaults can't silently
              // revoke it (absent key === denied in the permission resolver).
              'hq.reports.salesCommission': true,
              'hq.reports.leads': true,        // Own leads only
              'hq.reports.myAllocatedUnits': true,  // Own allocated units
              'hq.reports.statsHistory': false
            }
          },
          'hq.development': { enabled: false, subItems: {} },
          'hq.payments': { enabled: false, subItems: {} },
          'hq.statistics': { enabled: false, subItems: {} },
          'hq.templates': { enabled: false, subItems: {} },
          'hq.developmentDocs': { enabled: false, subItems: {} }
        }
      },
      crm: {
        enabled: true,
        menuGroups: {
          'crm.contacts': {
            enabled: true,
            subItems: {
              'crm.contacts.view': true,
              'crm.contacts.add': true,
              'crm.contacts.import': false
            }
          },
          'crm.leads': {
            enabled: true,
            subItems: {
              'crm.leads.view': true,
              'crm.leads.assign': false,
              'crm.leads.convert': true
            }
          },
          'crm.activities': {
            enabled: true,
            subItems: {
              'crm.activities.view': true,
              'crm.activities.schedule': true
            }
          }
        }
      },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: false, menuGroups: {} },  // Consultants should not see Lead Stats dashboard card
      viewingManagement: { enabled: false, menuGroups: {} },
      consultations: { enabled: true, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} },
      credit_score_view: { enabled: true, menuGroups: {} },
      my_monthly_review: { enabled: true, menuGroups: {} },
      expired_reservations: { enabled: true, menuGroups: {} },
      escalations: { enabled: true, menuGroups: {} },
      booking_outcomes: { enabled: true, menuGroups: {} },
      performance_hub: { enabled: true, menuGroups: {} },
      investmentProposals: { enabled: true, menuGroups: {} },
      // Narrowed to the capital consultant by useCanAccessCapitalInvestment.
      capital_investment: { enabled: true, menuGroups: {} }
    }
  },

  // HQ Viewing Coordinator - Complete oversight of all viewings
  [UserRole.HQ_VIEWING_COORDINATOR]: {
    dashboardCards: {
      hq: {
        enabled: true,
        menuGroups: {
          'hq.salesTools': {
            enabled: true,
            subItems: {
              'hq.salesTools.manageReservations': false,
              'hq.salesTools.manageClientResponses': false,
              'hq.salesTools.manageOTP': true  // Viewing coordinators manage OTP documents
            }
          }
        }
      },
      crm: { enabled: false, menuGroups: {} },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: false, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      viewingOversight: { enabled: true, menuGroups: {} },
      consultations: { enabled: false, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: false, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  },

  // Call Centre Agent - Focused dashboard with stats and calendar booking
  [UserRole.HQ_CALL_CENTRE_AGENT]: {
    dashboardCards: {
      hq: { enabled: false, menuGroups: {} },
      crm: { enabled: false, menuGroups: {} },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: false, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      viewingOversight: { enabled: false, menuGroups: {} },
      consultations: { enabled: false, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  },

  // AI Agent (Level 1) — ARIA. Server-side actor: this account never logs in
  // interactively; these permissions exist for the impersonation/"view as"
  // path (admins inspecting her dashboard) and as the seed for the
  // rolePermissions/ai_agent_lvl1 doc. Read-oriented: CRM + consultations
  // visible; NO sales tools, NO reports, NO admin surfaces.
  [UserRole.AI_AGENT_LVL1]: {
    dashboardCards: {
      hq: { enabled: false, menuGroups: {} },
      crm: {
        enabled: true,
        menuGroups: {
          'crm.contacts': {
            enabled: true,
            subItems: {
              'crm.contacts.view': true,
              'crm.contacts.add': false,
              'crm.contacts.import': false
            }
          },
          'crm.leads': {
            enabled: true,
            subItems: {
              'crm.leads.view': true,
              'crm.leads.assign': false,
              'crm.leads.convert': false
            }
          },
          'crm.activities': {
            enabled: true,
            subItems: {
              'crm.activities.view': true,
              'crm.activities.schedule': false
            }
          }
        }
      },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: false, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      viewingOversight: { enabled: false, menuGroups: {} },
      consultations: { enabled: true, menuGroups: {} },
      training: { enabled: false, menuGroups: {} },
      my_documents: { enabled: false, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  },

  // HQ Manager - Full document management access
  [UserRole.HQ_MANAGER]: {
    dashboardCards: {
      cindy_analytics: { enabled: true, menuGroups: {} },
      cindy_cost_monitor: { enabled: true, menuGroups: {} },
      aria_mission_control: { enabled: true, menuGroups: {} },
      hq: {
        enabled: true,
        menuGroups: {
          'hq.development': {
            enabled: true,
            subItems: {
              'hq.development.search': true,
              'hq.development.create': true,
              'hq.development.edit': true,
              'hq.development.editUnits': true,
              'hq.development.viewIncome': true,
              'hq.development.allIncome': true,
              'hq.development.delete': true
            }
          },
          'hq.payments': {
            enabled: true,
            subItems: {
              'hq.payments.proforma': true,
              'hq.payments.invoice': true,
              'hq.payments.payAgent': true,
              'hq.payments.payPartner': true,
              'hq.payments.manageReservationAmounts': true,
              'hq.payments.consultantLoans': true
            }
          },
          'hq.salesTools': {
            enabled: true,
            subItems: {
              'hq.salesTools.preReserve': true,
              'hq.salesTools.bulkPreReserve': true,
              'hq.salesTools.reserve': true,
              'hq.salesTools.captureDeal': true,
              'hq.salesTools.cancelDeal': true,
              'hq.salesTools.bookViewing': true,
              'hq.salesTools.manageReservations': true,
              'hq.salesTools.manageClientResponses': true,
              'hq.salesTools.manageOTP': true,
              'hq.salesTools.expiredReservations': true
            }
          },
          'hq.leads': {
            enabled: true,
            subItems: {
              'hq.leads.manualCapture': true,
              'hq.leads.import': false,
              'hq.leads.assign': false,
              'hq.leads.creditScores': true
            }
          },
          'hq.reports': {
            enabled: true,
            subItems: {
              'hq.reports.sales': true,
              'hq.reports.commissions': true,
              'hq.reports.bonds': true,
              'hq.reports.bondsCommission': true,
              'hq.reports.cash': true,
              'hq.reports.cancelled': true,
              'hq.reports.mandates': true,
              'hq.reports.loans': true,
              'hq.reports.salesCommission': true,
              'hq.reports.leads': true,
              'hq.reports.myAllocatedUnits': true,
              'hq.reports.depositWaiver': true
            }
          },
          'hq.statistics': {
            enabled: true,
            subItems: {
              'hq.statistics.statsHistoryHQ': true,
              'hq.statistics.statsHistoryConsultant': true,
              'hq.statistics.statsHistoryIndependent': true,
              'hq.statistics.statsHistoryPartner': true,
              'hq.statistics.statsHistoryPartnerAgent': true
            }
          },
          'hq.templates': {
            enabled: true,
            subItems: {
              'hq.templates.mandate': true,
              'hq.templates.reservation': true,
              'hq.templates.consultantContract': true,
              'hq.templates.independentConsultantContract': true,
              'hq.templates.adminContract': true,
              'hq.templates.independentPartnerContract': true
            }
          },
          'hq.developmentDocs': {
            enabled: true,
            subItems: {
              'hq.developmentDocs.viewDocs': true
            }
          },
        }
      },
      crm: {
        enabled: true,
        menuGroups: {
          'crm.contacts': {
            enabled: true,
            subItems: {
              'crm.contacts.view': true,
              'crm.contacts.add': true,
              'crm.contacts.import': true
            }
          },
          'crm.leads': {
            enabled: true,
            subItems: {
              'crm.leads.view': true,
              'crm.leads.assign': true,
              'crm.leads.convert': true
            }
          },
          'crm.activities': {
            enabled: true,
            subItems: {
              'crm.activities.view': true,
              'crm.activities.schedule': true
            }
          }
        }
      },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: true, menuGroups: {} },
      leadStats: { enabled: true, menuGroups: {} },
      viewingManagement: { enabled: true, menuGroups: {} },
      consultations: { enabled: true, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      syndication_config: { enabled: true, menuGroups: {} },
      whatsapp_followups_config: { enabled: true, menuGroups: {} },
      whatsapp_bot_builder: { enabled: false, menuGroups: {} },
      whatsapp_automation_center: { enabled: false, menuGroups: {} },
      whatsapp_bot_analytics: { enabled: true, menuGroups: {} },
      reviews_admin: { enabled: true, menuGroups: {} },
      consultant_reviews: { enabled: true, menuGroups: {} },
      call_centre_config: { enabled: true, menuGroups: {} },
      dialer_simulation: { enabled: true, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} },
      credit_score_view: { enabled: true, menuGroups: {} },
      consultant_lead_report: { enabled: true, menuGroups: {} },
      investmentProposals: { enabled: true, menuGroups: {} },
      allProposals: { enabled: true, menuGroups: {} },
      expired_reservations: { enabled: true, menuGroups: {} },
      escalations: { enabled: true, menuGroups: {} },
      booking_outcomes: { enabled: true, menuGroups: {} }
    }
  },

  // HQ Admin - Full document management access
  [UserRole.HQ_ADMIN]: {
    dashboardCards: {
      hq: {
        enabled: true,
        menuGroups: {
          'hq.development': {
            enabled: true,
            subItems: {
              'hq.development.search': true,
              'hq.development.create': true,
              'hq.development.edit': true,
              'hq.development.editUnits': true,
              'hq.development.viewIncome': true,
              'hq.development.allIncome': true,
              'hq.development.delete': false
            }
          },
          'hq.payments': {
            enabled: true,
            subItems: {
              'hq.payments.proforma': true,
              'hq.payments.invoice': true,
              'hq.payments.payAgent': true,
              'hq.payments.payPartner': true,
              'hq.payments.manageReservationAmounts': true,
              'hq.payments.consultantLoans': true
            }
          },
          'hq.salesTools': {
            enabled: true,
            subItems: {
              'hq.salesTools.preReserve': true,
              'hq.salesTools.bulkPreReserve': true,
              'hq.salesTools.reserve': true,
              'hq.salesTools.captureDeal': true,
              'hq.salesTools.cancelDeal': true,
              'hq.salesTools.bookViewing': true,
              'hq.salesTools.manageReservations': true,
              'hq.salesTools.manageClientResponses': true,
              'hq.salesTools.manageOTP': true
            }
          },
          'hq.leads': {
            enabled: true,
            subItems: {
              'hq.leads.manualCapture': true,
              'hq.leads.import': false,
              'hq.leads.assign': false,
              'hq.leads.creditScores': true
            }
          },
          'hq.reports': {
            enabled: true,
            subItems: {
              'hq.reports.sales': true,
              'hq.reports.commissions': true,
              'hq.reports.bonds': true,
              'hq.reports.bondsCommission': true,
              'hq.reports.cash': true,
              'hq.reports.cancelled': true,
              'hq.reports.mandates': true,
              'hq.reports.loans': true,
              'hq.reports.salesCommission': true,
              'hq.reports.leads': true,
              'hq.reports.myAllocatedUnits': true,
              'hq.reports.depositWaiver': true
            }
          },
          'hq.statistics': {
            enabled: true,
            subItems: {
              'hq.statistics.statsHistoryHQ': true,
              'hq.statistics.statsHistoryConsultant': true,
              'hq.statistics.statsHistoryIndependent': true,
              'hq.statistics.statsHistoryPartner': true,
              'hq.statistics.statsHistoryPartnerAgent': true
            }
          },
          'hq.templates': {
            enabled: true,
            subItems: {
              'hq.templates.mandate': true,
              'hq.templates.reservation': true,
              'hq.templates.consultantContract': true,
              'hq.templates.independentConsultantContract': true,
              'hq.templates.adminContract': true,
              'hq.templates.independentPartnerContract': true
            }
          },
          'hq.developmentDocs': {
            enabled: true,
            subItems: {
              'hq.developmentDocs.viewDocs': true
            }
          },
        }
      },
      crm: {
        enabled: true,
        menuGroups: {
          'crm.contacts': {
            enabled: true,
            subItems: {
              'crm.contacts.view': true,
              'crm.contacts.add': true,
              'crm.contacts.import': true
            }
          },
          'crm.leads': {
            enabled: true,
            subItems: {
              'crm.leads.view': true,
              'crm.leads.assign': true,
              'crm.leads.convert': true
            }
          },
          'crm.activities': {
            enabled: true,
            subItems: {
              'crm.activities.view': true,
              'crm.activities.schedule': true
            }
          }
        }
      },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: true, menuGroups: {} },
      leadStats: { enabled: true, menuGroups: {} },
      viewingManagement: { enabled: true, menuGroups: {} },
      consultations: { enabled: true, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      syndication_config: { enabled: true, menuGroups: {} },
      whatsapp_followups_config: { enabled: true, menuGroups: {} },
      whatsapp_bot_builder: { enabled: false, menuGroups: {} },
      whatsapp_automation_center: { enabled: false, menuGroups: {} },
      whatsapp_bot_analytics: { enabled: false, menuGroups: {} },
      reviews_admin: { enabled: true, menuGroups: {} },
      consultant_reviews: { enabled: true, menuGroups: {} },
      call_centre_config: { enabled: true, menuGroups: {} },
      dialer_simulation: { enabled: true, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} },
      credit_score_view: { enabled: true, menuGroups: {} },
      investmentProposals: { enabled: true, menuGroups: {} },
      allProposals: { enabled: true, menuGroups: {} }
    }
  },

  // PropCloudAI User Management - Full document management access
  [UserRole.PROPCLOUDAI_USER_MANAGEMENT]: {
    dashboardCards: {
      hq: {
        enabled: true,
        menuGroups: {
          'hq.salesTools': {
            enabled: true,
            subItems: {
              'hq.salesTools.manageReservations': true,
              'hq.salesTools.manageClientResponses': true,
              'hq.salesTools.manageOTP': true
            }
          }
        }
      },
      crm: { enabled: false, menuGroups: {} },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: false, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      consultations: { enabled: false, menuGroups: {} },
      training: { enabled: false, menuGroups: {} },
      my_documents: { enabled: false, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  },

  // HQ Bonds - No document management access
  [UserRole.HQ_BONDS]: {
    dashboardCards: {
      hq: {
        enabled: true,
        menuGroups: {
          'hq.salesTools': {
            enabled: true,
            subItems: {
              'hq.salesTools.manageReservations': false,
              'hq.salesTools.manageClientResponses': false,
              'hq.salesTools.manageOTP': false
            }
          },
          'hq.reports': {
            enabled: true,
            subItems: {
              'hq.reports.bonds': true,
              'hq.reports.bondsCommission': true
            }
          }
        }
      },
      crm: { enabled: false, menuGroups: {} },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: false, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      consultations: { enabled: false, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: false, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  },

  // HQ Independent Consultant LVL2 - No document management access
  [UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]: {
    dashboardCards: {
      hq: {
        enabled: true,
        menuGroups: {
          'hq.salesTools': {
            enabled: true,
            subItems: {
              'hq.salesTools.preReserve': true,
              'hq.salesTools.bulkPreReserve': true,
              'hq.salesTools.reserve': false,  // Consultants cannot reserve, only pre-reserve
              'hq.salesTools.captureDeal': true,
              'hq.salesTools.bookViewing': true,
              'hq.salesTools.manageReservations': false,
              'hq.salesTools.manageClientResponses': false,
              'hq.salesTools.manageOTP': false,
              'hq.salesTools.expiredReservations': true  // Own snapshots only (enforced by CF + list scoping)
            }
          },
          'hq.reports': {
            enabled: true,
            subItems: {
              'hq.reports.sales': true,        // Own sales only
              'hq.reports.commissions': true,  // Own commissions only
              // Own sales-commission report only — kept in sync with the live
              // rolePermissions doc so a reset can't silently revoke it.
              'hq.reports.salesCommission': true,
              'hq.reports.leads': true,        // Own leads only
              'hq.reports.myAllocatedUnits': true  // Own allocated units
            }
          },
          'hq.development': { enabled: false, subItems: {} },
          'hq.payments': { enabled: false, subItems: {} },
          'hq.statistics': { enabled: false, subItems: {} },
          'hq.templates': { enabled: false, subItems: {} },
          'hq.developmentDocs': { enabled: false, subItems: {} },
        }
      },
      crm: {
        enabled: true,
        menuGroups: {
          'crm.contacts': {
            enabled: true,
            subItems: {
              'crm.contacts.view': true,
              'crm.contacts.add': true,
              'crm.contacts.import': false
            }
          },
          'crm.leads': {
            enabled: true,
            subItems: {
              'crm.leads.view': true,
              'crm.leads.assign': false,
              'crm.leads.convert': true
            }
          }
        }
      },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: false, menuGroups: {} },  // Consultants should not see Lead Stats dashboard card
      viewingManagement: { enabled: false, menuGroups: {} },
      consultations: { enabled: true, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} },
      my_monthly_review: { enabled: true, menuGroups: {} },
      expired_reservations: { enabled: true, menuGroups: {} },
      escalations: { enabled: true, menuGroups: {} },
      booking_outcomes: { enabled: true, menuGroups: {} },
      performance_hub: { enabled: true, menuGroups: {} },
      investmentProposals: { enabled: true, menuGroups: {} },
      capital_investment: { enabled: true, menuGroups: {} }
    }
  },

  // HQ Referral Mandate - No document management access
  [UserRole.HQ_REFERRAL_MANDATE]: {
    dashboardCards: {
      hq: {
        enabled: true,
        menuGroups: {
          'hq.salesTools': {
            enabled: false,
            subItems: {
              'hq.salesTools.manageReservations': false,
              'hq.salesTools.manageClientResponses': false,
              'hq.salesTools.manageOTP': false
            }
          },
          'hq.reports': {
            enabled: true,
            subItems: {
              'hq.reports.mandates': true,
              'hq.reports.commissions': true
            }
          }
        }
      },
      crm: {
        enabled: true,
        menuGroups: {
          'crm.contacts': {
            enabled: true,
            subItems: {
              'crm.contacts.view': true,
              'crm.contacts.add': true
            }
          }
        }
      },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: true, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      consultations: { enabled: false, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  },

  // Independent Partner LVL3 - No document management access
  [UserRole.INDEPENDENT_PARTNER_LVL3]: {
    dashboardCards: {
      hq: { enabled: false, menuGroups: {} },
      crm: {
        enabled: true,
        menuGroups: {
          'crm.contacts': {
            enabled: true,
            subItems: {
              'crm.contacts.view': true,
              'crm.contacts.add': true
            }
          }
        }
      },
      partner: {
        enabled: true,
        menuGroups: {
          'partner.salesTools': {
            enabled: true,
            subItems: {
              'partner.salesTools.preReserve': true,
              'partner.salesTools.reserve': true,
              'partner.salesTools.captureDeal': true
            }
          },
          'partner.reports': {
            enabled: true,
            subItems: {
              'partner.reports.sales': true,
              'partner.reports.commissions': true
            }
          },
          'partner.statistics': {
            enabled: true,
            subItems: {
              'partner.statistics.performance': true
            }
          }
        }
      },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: true, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      consultations: { enabled: true, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} },
      credit_score_view: { enabled: true, menuGroups: {} }
    }
  },

  // Partner Agent LVL3 - No document management access
  [UserRole.PARTNER_AGENT_LVL3]: {
    dashboardCards: {
      hq: { enabled: false, menuGroups: {} },
      crm: {
        enabled: true,
        menuGroups: {
          'crm.contacts': {
            enabled: true,
            subItems: {
              'crm.contacts.view': true,
              'crm.contacts.add': true
            }
          }
        }
      },
      partner: {
        enabled: true,
        menuGroups: {
          'partner.salesTools': {
            enabled: true,
            subItems: {
              'partner.salesTools.preReserve': true,
              'partner.salesTools.reserve': true,
              'partner.salesTools.captureDeal': true
            }
          },
          'partner.reports': {
            enabled: true,
            subItems: {
              'partner.reports.sales': true,
              'partner.reports.commissions': true
            }
          }
        }
      },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: true, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      consultations: { enabled: true, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} },
      credit_score_view: { enabled: true, menuGroups: {} }
    }
  },

  // Viewing Agent - No document management access
  [UserRole.VIEWING_AGENT]: {
    dashboardCards: {
      hq: { enabled: false, menuGroups: {} },
      crm: { enabled: false, menuGroups: {} },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: false, menuGroups: {} },
      viewingManagement: { enabled: true, menuGroups: {} },
      consultations: { enabled: false, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: false, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  },

  // PropCloudAI CRM - No document management access
  [UserRole.PROPCLOUDAI_CRM]: {
    dashboardCards: {
      hq: { enabled: false, menuGroups: {} },
      crm: {
        enabled: true,
        menuGroups: {
          'crm.contacts': {
            enabled: true,
            subItems: {
              'crm.contacts.view': true,
              'crm.contacts.add': true,
              'crm.contacts.import': true
            }
          },
          'crm.leads': {
            enabled: true,
            subItems: {
              'crm.leads.view': true,
              'crm.leads.assign': true,
              'crm.leads.convert': true
            }
          },
          'crm.activities': {
            enabled: true,
            subItems: {
              'crm.activities.view': true,
              'crm.activities.schedule': true
            }
          }
        }
      },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: true, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      consultations: { enabled: false, menuGroups: {} },
      training: { enabled: false, menuGroups: {} },
      my_documents: { enabled: false, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  },

  // PropCloudAI Advertising - Deprecated, use HQ_ADVERTISING
  [UserRole.PROPCLOUDAI_ADVERTISING]: {
    dashboardCards: {
      hq: { enabled: false, menuGroups: {} },
      crm: { enabled: false, menuGroups: {} },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: true, menuGroups: {} },
      leadStats: { enabled: true, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      consultations: { enabled: false, menuGroups: {} },
      training: { enabled: false, menuGroups: {} },
      my_documents: { enabled: false, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  },

  // HQ Advertising - Marketing & advertising team
  [UserRole.HQ_ADVERTISING]: {
    dashboardCards: {
      hq: {
        enabled: true,
        menuGroups: {
          'hq.development': {
            enabled: true,
            subItems: {
              'hq.development.search': true,
              'hq.development.create': false,
              'hq.development.edit': false,
              'hq.development.editUnits': false,
              'hq.development.viewIncome': false,
              'hq.development.allIncome': false,
              'hq.development.delete': false
            }
          },
          'hq.developmentDocs': {
            enabled: true,
            subItems: {
              'hq.developmentDocs.viewDocs': true
            }
          },
          'hq.salesTools': { enabled: false, subItems: {} },
          'hq.payments': { enabled: false, subItems: {} },
          'hq.reports': { enabled: false, subItems: {} },
          'hq.statistics': { enabled: false, subItems: {} },
          'hq.templates': { enabled: false, subItems: {} },
        }
      },
      crm: { enabled: false, menuGroups: {} },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: true, menuGroups: {} },
      leadStats: { enabled: true, menuGroups: {} },
      viewingManagement: { enabled: false, menuGroups: {} },
      consultations: { enabled: false, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  },

  // HQ Viewing Agent - Property viewing management
  [UserRole.HQ_VIEWING_AGENT]: {
    dashboardCards: {
      hq: { enabled: false, menuGroups: {} },
      crm: { enabled: false, menuGroups: {} },
      partner: { enabled: false, menuGroups: {} },
      advertising: { enabled: false, menuGroups: {} },
      leadStats: { enabled: false, menuGroups: {} },
      viewingManagement: { enabled: true, menuGroups: {} },
      consultations: { enabled: false, menuGroups: {} },
      training: { enabled: true, menuGroups: {} },
      my_documents: { enabled: true, menuGroups: {} },
      website_cms: { enabled: false, menuGroups: {} },
      mobile_app_cms: { enabled: false, menuGroups: {} },
      media_manager: { enabled: false, menuGroups: {} }
    }
  }
};

// Helper function to get menu permissions for a role
export const getMenuPermissionsForRole = (role: UserRole | string, cardId: string, groupId?: string) => {
  const rolePermissions = DEFAULT_HIERARCHICAL_PERMISSIONS[role];
  if (!rolePermissions) return { enabled: false, subItems: {} };

  const cardPermissions = rolePermissions.dashboardCards[cardId];
  if (!cardPermissions || !cardPermissions.enabled) return { enabled: false, subItems: {} };

  if (groupId) {
    return cardPermissions.menuGroups[groupId] || { enabled: false, subItems: {} };
  }

  return cardPermissions;
};

// Helper function to check if a role has permission for a specific card
export const roleHasCardAccess = (role: UserRole | string, cardId: string): boolean => {
  const permissions = getMenuPermissionsForRole(role, cardId);
  return permissions.enabled === true;
};

// Helper function to check if a role has permission for a specific menu item
export const roleHasMenuItemAccess = (role: UserRole | string, cardId: string, groupId: string, itemId: string): boolean => {
  const groupPermissions = getMenuPermissionsForRole(role, cardId, groupId);
  if (!groupPermissions.enabled) return false;
  return groupPermissions.subItems?.[itemId] === true;
};